// ---- Firestarting (client) --------------------------------------------------
// Lightweight activity: bag-only start (kindling selected → "Start fire") then a
// forced placement pick — four ORANGE tiles 1u from the player's feet, laid out
// relative to the CAMERA (W = away/top of screen, A left, S toward, D right).
// Click or WASD → fireStart {x,z} → spark minigame. Materials spend ONLY on
// success (server). No action-bar Start Fire slot.
//
// Phases:
//   placing  — orange selector HUD/tiles; game.placingFire locks movement
//   session  — minigame live; game.fireStarting locks + kneel
//
// Never leave game.fireStarting true without a live minigame session.
// Init AFTER initInteract, BEFORE initFocus (click + Esc precedence).

import * as THREE from "three";
import { game, inputLocked } from "./state.ts";
import { sendFireStart, sendFireInput, sendFireEnd } from "./net.ts";
import { makeFireStartMinigame } from "./minigame/firestart.ts";
import type { Minigame } from "./minigame/types.ts";
import { genFirePattern } from "./sim/firestarting.ts";
import { FIRESTARTING_TUNING as FT, COOKING_TUNING as CT } from "./tunables.ts";
import { startAttack } from "./player.ts";
import { flashToast, countOf, closeInventory } from "./inventory.ts";
import { heightAt, WATER_LEVEL } from "./terrain.ts";
import { structureKeepOuts } from "./world.ts";
import { WATER_MARGIN } from "./sim/fishing.ts";
import { LOG_ITEM_TYPES } from "./items/define.ts";
import { DECOS } from "./deco/index.ts";
import { buildDeco } from "./deco/define.ts";
import { disposeGroup } from "./meshkit.ts";
import { chatTyping } from "./chat.ts";
import { playSfx, preloadSfx } from "./audio.ts";
import type { ItemType } from "./shared.ts";
import { registerHoverProbe } from "./cursor.ts";

/** Kindling-strike one-shot — same cadence as woodcutting's chop SFX. */
const KINDLE_SFX = "kindling-striking";

/** Distance from feet to each placement tile (world units). */
export const FIRE_PLACE_DIST = 1;

/** Placement key → tile index: 0=W(forward), 1=D(right), 2=S(back), 3=A(left). */
const PLACE_KEY_IDX: Record<string, number> = {
  w: 0, W: 0, ArrowUp: 0,
  d: 1, D: 1, ArrowRight: 1,
  s: 2, S: 2, ArrowDown: 2,
  a: 3, A: 3, ArrowLeft: 3,
};

const PLACE_LETTERS = ["W", "D", "S", "A"] as const;

const _camDir = new THREE.Vector3();

/** Camera-forward and camera-right on the XZ ground plane (Y-up). */
export function cameraBasisXZ(cam: THREE.Camera): {
  fx: number; fz: number; rx: number; rz: number;
} {
  cam.getWorldDirection(_camDir);
  let fx = _camDir.x;
  let fz = _camDir.z;
  const len = Math.hypot(fx, fz);
  if (len < 1e-4) {
    // Looking straight down/up — fall back to camera yaw.
    const e = new THREE.Euler().setFromQuaternion(cam.quaternion, "YXZ");
    fx = Math.sin(e.y);
    fz = Math.cos(e.y);
  } else {
    fx /= len;
    fz /= len;
  }
  // right = forward × worldUp  →  (-fz, 0, fx)
  return { fx, fz, rx: -fz, rz: fx };
}

/**
 * Four spots 1u from the player, camera-relative:
 *   0 W = forward (top of view), 1 D = right, 2 S = back, 3 A = left.
 * Anchored on the player's feet so tiles sit snug around them (no grid snap).
 */
export function adjacentFireSpots(
  px: number,
  pz: number,
  cam: THREE.Camera,
  dist = FIRE_PLACE_DIST,
): { x: number; z: number; letter: string }[] {
  const { fx, fz, rx, rz } = cameraBasisXZ(cam);
  return [
    { x: px + fx * dist, z: pz + fz * dist, letter: "W" },
    { x: px + rx * dist, z: pz + rz * dist, letter: "D" },
    { x: px - fx * dist, z: pz - fz * dist, letter: "S" },
    { x: px - rx * dist, z: pz - rz * dist, letter: "A" },
  ];
}

/** Server-matching adjacency: ~1u from feet (camera orientation is client-only). */
export function isAdjacentFireSpot(px: number, pz: number, x: number, z: number): boolean {
  const d = Math.hypot(x - px, z - pz);
  return d >= FIRE_PLACE_DIST * 0.65 && d <= FIRE_PLACE_DIST * 1.4;
}

let session: {
  x: number;
  z: number;
  mg: Minigame & { setHintLabel?(s: string): void };
  pile: THREE.Group | null;
} | null = null;

/** Placement-pick phase (before the server session). */
let placing: {
  tiles: THREE.Mesh[];
  labels: THREE.Mesh[];
  group: THREE.Group;
  hint: HTMLElement;
  hover: number; // index into tiles, -1 = none
} | null = null;

let scene: THREE.Scene | null = null;
let camera: THREE.Camera | null = null;
let canvas: HTMLElement | null = null;
const raycaster = new THREE.Raycaster();
const ndc = new THREE.Vector2();

export function isFireStartingActive(): boolean {
  return !!session || game.fireStarting || !!placing || game.placingFire;
}

function dropPile(pile: THREE.Group | null): void {
  if (!pile) return;
  if (scene) scene.remove(pile);
  disposeGroup(pile);
}

function teardownSession(): void {
  if (session) {
    session.mg.unmount();
    dropPile(session.pile);
    session = null;
  }
  game.fireStarting = false;
  if (game.player) {
    game.player.userData.fireStarting = false;
    game.player.userData.bodyPose = null;
  }
  if (game.pose === "kneeling") game.pose = null;
}

function disposeMesh(m: THREE.Mesh): void {
  m.geometry.dispose();
  const mat = m.material as THREE.MeshBasicMaterial;
  mat.map?.dispose();
  mat.dispose();
}

function clearPlacement(): void {
  if (!placing) {
    game.placingFire = false;
    return;
  }
  placing.hint.remove();
  if (scene) scene.remove(placing.group);
  for (const m of placing.tiles) disposeMesh(m);
  for (const m of placing.labels) disposeMesh(m);
  placing = null;
  game.placingFire = false;
}

/** Flat ground-facing letter on a transparent plane (W/A/S/D). */
function makeKeyLabel(letter: string, ok: boolean): THREE.Mesh {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, size, size);
  ctx.font = "bold 92px ui-monospace, monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Dark outline so the glyph reads on orange and grey tiles.
  ctx.lineWidth = 10;
  ctx.strokeStyle = "rgba(0,0,0,0.75)";
  ctx.strokeText(letter, size / 2, size / 2 + 4);
  ctx.fillStyle = ok ? "#fff4e0" : "rgba(200,200,200,0.55)";
  ctx.fillText(letter, size / 2, size / 2 + 4);
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  const mat = new THREE.MeshBasicMaterial({
    map: tex,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(0.55, 0.55), mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.raycast = () => {}; // clicks hit the tile under the letter, not the glyph
  return mesh;
}

/** Dry ground outside structure keep-outs. */
export function canPlaceFireClient(x: number, z: number): "ok" | "water" | "structure" {
  if (heightAt(x, z) < WATER_LEVEL - WATER_MARGIN) return "water";
  for (const k of structureKeepOuts) {
    if (Math.hypot(x - k.x, z - k.z) < k.r) return "structure";
  }
  return "ok";
}

export function carryingFireMakings(): boolean {
  return LOG_ITEM_TYPES.some((t) => countOf(t as ItemType) > 0)
    && countOf("kindling" as ItemType) > 0;
}

function makeTileMat(ok: boolean, hover: boolean): THREE.MeshBasicMaterial {
  if (!ok) {
    return new THREE.MeshBasicMaterial({
      color: 0x444444,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }
  // Orange selector — brighter on hover.
  return new THREE.MeshBasicMaterial({
    color: hover ? 0xffaa33 : 0xff7a18,
    transparent: true,
    opacity: hover ? 0.72 : 0.48,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

function paintTile(mesh: THREE.Mesh, ok: boolean, hover: boolean): void {
  const old = mesh.material as THREE.Material;
  mesh.material = makeTileMat(ok, hover);
  old.dispose();
  mesh.userData.ok = ok;
  mesh.userData.hover = hover;
}

/** Bag "Start fire" — enter placement pick (no network yet). */
export function tryStartFire(): void {
  if (session || game.fireStarting || placing) return;
  if (game.down || game.talking || game.fishing || game.cooking || game.chopping || game.transferring || game.cinematic) {
    flashToast("You're busy");
    return;
  }
  if (!carryingFireMakings()) {
    flashToast("You need logs and kindling");
    return;
  }
  // Own fire already burning is enforced server-side; still soft-check outdoors.
  if (canPlaceFireClient(game.player.position.x, game.player.position.z) === "structure") {
    flashToast("Need open sky — step outdoors");
    return;
  }
  beginPlacement();
}

function beginPlacement(): void {
  if (!scene || !game.player || !camera) return;
  closeInventory();
  clearPlacement();

  const px = game.player.position.x;
  const pz = game.player.position.z;
  // Camera-relative ring around the feet (not a world-grid snap — that offset
  // the tiles when the player wasn't at a cell center).
  const spots = adjacentFireSpots(px, pz, camera);
  const group = new THREE.Group();
  group.name = "firePlaceTiles";
  const tiles: THREE.Mesh[] = [];
  const labels: THREE.Mesh[] = [];
  let anyOk = false;

  for (let i = 0; i < spots.length; i++) {
    const c = spots[i]!;
    const ok = canPlaceFireClient(c.x, c.z) === "ok";
    if (ok) anyOk = true;
    const geo = new THREE.PlaneGeometry(0.92, 0.92);
    const mesh = new THREE.Mesh(geo, makeTileMat(ok, false));
    mesh.rotation.x = -Math.PI / 2;
    const y = heightAt(c.x, c.z) + 0.06;
    mesh.position.set(c.x, y, c.z);
    mesh.userData.ok = ok;
    mesh.userData.tx = c.x;
    mesh.userData.tz = c.z;
    mesh.userData.hover = false;
    group.add(mesh);
    tiles.push(mesh);

    const label = makeKeyLabel(c.letter, ok);
    label.position.set(c.x, y + 0.03, c.z);
    group.add(label);
    labels.push(label);
  }

  if (!anyOk) {
    for (const m of tiles) disposeMesh(m);
    for (const m of labels) disposeMesh(m);
    flashToast("No clear adjacent ground for a fire");
    return;
  }

  scene.add(group);

  const hint = document.createElement("div");
  hint.id = "firePlaceHint";
  hint.textContent = "🔥 WASD / click a square next to you · Esc cancel";
  hint.style.cssText =
    "position:fixed;left:50%;bottom:150px;transform:translateX(-50%);z-index:55;" +
    "font:bold 14px ui-monospace,monospace;color:#ffc070;white-space:nowrap;" +
    "background:rgba(0,0,0,0.72);padding:10px 18px;border-radius:20px;" +
    "box-shadow:0 2px 12px #0008;border:1px solid rgba(255,140,40,0.55);" +
    "pointer-events:none;user-select:none;";
  document.body.appendChild(hint);

  placing = { tiles, labels, group, hint, hover: -1 };
  game.placingFire = true;
}

function pickPlacement(clientX: number, clientY: number): THREE.Mesh | null {
  if (!placing || !camera || !canvas) return null;
  const rect = canvas.getBoundingClientRect();
  ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
  ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(ndc, camera);
  const hits = raycaster.intersectObjects(placing.tiles, false);
  return hits[0]?.object as THREE.Mesh | undefined ?? null;
}

function confirmPlacement(mesh: THREE.Mesh): void {
  if (!mesh.userData.ok) {
    flashToast("Can't light a fire there");
    return;
  }
  const x = mesh.userData.tx as number;
  const z = mesh.userData.tz as number;
  clearPlacement();
  sendFireStart(x, z);
}

function buildUnlitPile(): THREE.Group {
  const g = new THREE.Group();
  const spec = DECOS.find((d) => d.type === "campfire");
  if (spec) g.add(buildDeco(spec, 3));
  return g;
}

/** Server accepted the session — mount the minigame. */
export function onFireOn(seed: number, x: number, z: number): void {
  clearPlacement();
  if (session) {
    session.mg.unmount();
    dropPile(session.pile);
    session = null;
  }
  // Warm the kindling one-shot so the first successful key isn't silent.
  preloadSfx(KINDLE_SFX);
  const pattern = genFirePattern(seed, FT);
  const mg = makeFireStartMinigame({
    pattern,
    hitWindow: FT.hitWindow,
    tun: FT,
    title: "🔥 SPARK — match the keys!",
    hintLabel: () => "kindling + logs · success lights the fire",
    ev: {
      onInput(i, hit, _finished) {
        if (!session) return;
        startAttack(game.player, "primary");
        // Successful key strike → flint/kindling spark (misses stay silent).
        if (hit) playSfx(KINDLE_SFX);
        sendFireInput(i, hit);
      },
    },
  });
  mg.mount();

  let pile: THREE.Group | null = null;
  if (scene) {
    pile = buildUnlitPile();
    pile.position.set(x, heightAt(x, z), z);
    scene.add(pile);
  }

  session = { x, z, mg, pile };
  game.fireStarting = true;
  game.pose = "kneeling";
  if (game.player) {
    game.player.userData.fireStarting = true;
    game.player.userData.bodyPose = "kneeling";
    game.player.rotation.y = Math.atan2(x - game.player.position.x, z - game.player.position.z);
  }
}

/** Server resolved the session. */
export function onFireResult(outcome: "lit" | "failed" | "invalid"): void {
  teardownSession();
  if (outcome === "lit") flashToast("Fire lit");
  else if (outcome === "failed") flashToast("The embers died");
}

export function cancelFireStart(): void {
  if (placing) {
    clearPlacement();
    return;
  }
  if (!session && !game.fireStarting) return;
  sendFireEnd();
  teardownSession();
}

export function initFirestarting(
  s: THREE.Scene,
  cam: THREE.Camera,
  gameCanvas: HTMLElement,
): void {
  scene = s;
  camera = cam;
  canvas = gameCanvas;

  addEventListener("keydown", (e) => {
    if (chatTyping()) return;
    if (e.key === "Escape" && (placing || session || game.fireStarting || game.placingFire)) {
      cancelFireStart();
      e.stopImmediatePropagation();
      return;
    }
    // Placement pick: W/A/S/D (and arrows) choose N/W/S/E adjacent tiles.
    if (placing) {
      const idx = PLACE_KEY_IDX[e.key];
      if (idx !== undefined) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const mesh = placing.tiles[idx];
        if (!mesh) return;
        // Visual flash on the chosen square, then confirm (or toast if invalid).
        if (placing.hover >= 0 && placing.hover !== idx) {
          const prev = placing.tiles[placing.hover]!;
          paintTile(prev, !!prev.userData.ok, false);
        }
        placing.hover = idx;
        if (mesh.userData.ok) paintTile(mesh, true, true);
        confirmPlacement(mesh);
        return;
      }
    }
    if (session?.mg.key(e)) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);

  // Placement clicks must win over focus/interact — capture on the canvas,
  // registered before initFocus (game.ts order).
  gameCanvas.addEventListener("pointerdown", (e) => {
    if (!placing || e.button !== 0) return;
    if (inputLocked() && !game.placingFire) return;
    const mesh = pickPlacement(e.clientX, e.clientY);
    if (!mesh) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    confirmPlacement(mesh);
  }, true);

  gameCanvas.addEventListener("pointermove", (e) => {
    if (!placing) return;
    const mesh = pickPlacement(e.clientX, e.clientY);
    const idx = mesh ? placing.tiles.indexOf(mesh) : -1;
    if (idx === placing.hover) return;
    // Clear old hover.
    if (placing.hover >= 0) {
      const prev = placing.tiles[placing.hover]!;
      paintTile(prev, !!prev.userData.ok, false);
    }
    placing.hover = idx;
    if (idx >= 0) {
      const cur = placing.tiles[idx]!;
      if (cur.userData.ok) paintTile(cur, true, true);
    }
  });

  // Placement tiles are click targets — light the pointer while hovering one
  // (src/cursor.ts owns canvas.style.cursor; do not write it here).
  registerHoverProbe((rc) => {
    if (!placing) return false;
    return rc.intersectObjects(placing.tiles, false).length > 0;
  });
}

export function updateFirestarting(dt: number): void {
  // Placement: keep tiles glued to ground if terrain under feet changes (rare).
  if (placing && game.player) {
    // If player somehow moved (shouldn't while locked), cancel — tiles are fixed
    // to the cell they opened on.
    void dt;
  }

  if (game.fireStarting && !session) {
    teardownSession();
    return;
  }
  if (!session) return;
  session.mg.tick(dt);
  const q = game.player.position;
  game.player.rotation.y = Math.atan2(session.x - q.x, session.z - q.z);
  game.player.userData.fireStarting = true;
  game.player.userData.bodyPose = "kneeling";
  game.pose = "kneeling";
  if (Math.hypot(q.x - session.x, q.z - session.z) > CT.fireRange + 2) {
    cancelFireStart();
  }
}

export function resetFirestarting(): void {
  clearPlacement();
  teardownSession();
}
